import { useState, useEffect, useRef } from "react";
import { MagnifyingGlass } from "react-loader-spinner";
import Footer from "./Footer";
import SafeHTML from "../../../components/SafeHTML";
import { useMyKadService } from "../useMyKadService";
import { nextProgress } from "../verifyingProgress";

const TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes
const FINAL_CHECK_GRACE_MS = 15 * 1000; // the last result check gets at least this long from its start
const LONG_WAIT_MS = 20 * 1000; // 20 seconds

const Verifying = ({
  validationID,
  progressStates,
  onPass,
  onFail,
  onSessionUpdate,
  uiConfig,
}) => {
  // Safe access
  const global = uiConfig?.Global || {};
  const verifying = uiConfig?.Verifying || {};
  const cardBgColor = global?.card?.backgroundColor || "#f3f4f6"; // gray-50
  const cardOpacity = global?.card?.opacity || "1"; // default opacity
  const pageBgColor = global?.pageContainer?.backgroundColor || "#ffffff"; // white
  const textColor = global?.text?.fontColor || "#6b7280"; // default gray-500
  const highlightTextColor = global?.highlightText?.fontColor || "#1d4ed8"; // blue-700
  const showFooter = global?.Footer?.Show !== undefined ? global.Footer.Show : true;

  const getTitleHTML = () => {
    const titleConfig = verifying?.Title?.Text;
    if (titleConfig) {
      return titleConfig;
    }
    return `<p style="font-size: 1.5rem; font-weight: 500; line-height: 2rem; color: ${textColor};">
      Verifying your
      <br />
      <span style="color: ${highlightTextColor};">MyKad.</span>
    </p>`;
  };

  const getDescriptionHTML = () => {
    const descriptionConfig = verifying?.Description?.Text;
    if (descriptionConfig) {
      return descriptionConfig;
    }
    return `<p style="font-size: 1rem; margin-top: 1rem; color: ${textColor};">
      Please wait a moment while our secure system processes your information...
    </p>`;
  };

  const { checkResult } = useMyKadService();

  const [startVerify, setStartVerify] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showLongWaitMessage, setShowLongWaitMessage] = useState(false);
  const isVerifying = useRef(false);
  const deadlineRef = useRef(null);
  const longWaitRef = useRef(null);

  // When this screen mounted. The long-wait message and the timeout count time on this screen
  // only; Pending polls keep it, and a retake mounts a new screen with a new start time.
  const [startTime] = useState(() => Date.now());

  // The 2-minute timeout is never declared without asking the server (#111). A response that
  // arrives after the deadline (one still in flight when it passed, or the one more check made
  // after it) is the last one: a Pass or In Review decides as usual, and a Fail does when both
  // sides have their results (front and back each Pass or Fail) or the attempt was the last one
  // allowed (the parent then shows the max-retry screen). Anything else is the timeout. Each poll's request gets until TIMEOUT_MS + FINAL_CHECK_GRACE_MS after startTime,
  // and at least FINAL_CHECK_GRACE_MS, to answer; one that has not answered by then is the
  // timeout too, so a request that never answers cannot leave this screen waiting.
  const isPastDeadline = () => Date.now() - startTime > TIMEOUT_MS;
  const isSettled = (status) => status === "Pass" || status === "Fail";

  const clearDeadlineTimer = () => {
    if (deadlineRef.current) {
      clearTimeout(deadlineRef.current);
      deadlineRef.current = null;
    }
  };

  useEffect(() => {
    setStartVerify(true);
    return () => {
      isVerifying.current = false;
      clearDeadlineTimer();
      if (longWaitRef.current) {
        clearTimeout(longWaitRef.current);
        longWaitRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (
      progressStates.myKadFront === "Pending" ||
      progressStates.myKadBack === "Pending"
    ) {
      setStartVerify(true);
    }
  }, [progressStates]);

  useEffect(() => {
    if (startVerify) {
      verifyDocument();
    }
  }, [startVerify]);

  const verifyDocument = async () => {
    isVerifying.current = true;
    setShowLongWaitMessage(false);

    clearDeadlineTimer();
    if (longWaitRef.current) {
      clearTimeout(longWaitRef.current);
      longWaitRef.current = null;
    }

    const elapsedTime = Date.now() - startTime;

    // Check if we should show long wait message
    if (elapsedTime > LONG_WAIT_MS) {
      setShowLongWaitMessage(true);
    }

    // One live timer per screen, armed for this poll's request (see isPastDeadline above).
    deadlineRef.current = setTimeout(
      () => {
        deadlineRef.current = null;
        if (isVerifying.current) {
          isVerifying.current = false;
          handleVerificationError(true);
        }
      },
      Math.max(TIMEOUT_MS + FINAL_CHECK_GRACE_MS - elapsedTime, FINAL_CHECK_GRACE_MS)
    );

    // Setup long wait message for remaining time if not already shown
    if (elapsedTime <= LONG_WAIT_MS) {
      const remainingLongWaitTime = LONG_WAIT_MS - elapsedTime;
      longWaitRef.current = setTimeout(() => {
        if (isVerifying.current) {
          setShowLongWaitMessage(true);
        }
      }, remainingLongWaitTime);
    }

    try {
      const res = await checkResult({
        validationID,
      });
      onSessionUpdate?.(res.session);

      if (!isVerifying.current) return;

      const isLastCheck = isPastDeadline();

      clearDeadlineTimer();
      if (longWaitRef.current) {
        clearTimeout(longWaitRef.current);
        longWaitRef.current = null;
      }

      if (res.status === "success") {
        // Update Progress. Never backward on this screen (#111); a retake shows a new screen,
        // which starts again from 0.
        if (res.data?.overallProgress) {
          const { done, total } = res.data.overallProgress;
          setProgress((shown) => nextProgress(shown, done, total));
        }

        // Check Status
        const overallStatus = res.data?.overallStatus;
        if (overallStatus === "Pass" || overallStatus === "In Review") {
          isVerifying.current = false;
          onPass(res.data);
          setStartVerify(false);
        } else if (
          isLastCheck &&
          !(
            overallStatus === "Fail" &&
            ((isSettled(res.data?.front?.status) && isSettled(res.data?.back?.status)) ||
              // Same max-retry comparison as MyKad.js's onFail.
              res.data?.validationCount >= 3)
          )
        ) {
          isVerifying.current = false;
          handleVerificationError(true, res.data);
          setStartVerify(false);
        } else {
          // Pass the result back to parent to handle polling loop or failure
          isVerifying.current = false;

          onFail(res.data);
          setStartVerify(false);
        }
      } else {
        // Server returned error status
        isVerifying.current = false;
        handleVerificationError(isLastCheck);
        setStartVerify(false);
      }
    } catch (error) {
      // Network or other error
      if (isVerifying.current) {
        isVerifying.current = false;
        handleVerificationError(isPastDeadline());
        setStartVerify(false);
      }
    }
  };

  // `data`: the deciding check-result, when there is one; its per-side statuses are newer than
  // progressStates (the poll before it), so "Try Again" retries the side that is really unfinished.
  const handleVerificationError = (isTimeout = false, data = null) => {
    clearDeadlineTimer();
    if (longWaitRef.current) {
      clearTimeout(longWaitRef.current);
      longWaitRef.current = null;
    }

    const getStatus = (status) => (status === "Pass" ? "Pass" : "Fail");

    // Construct a fail object based on current state to safely trigger failure in parent
    const failResult = {
      isTimeout,
      overallStatus: "Fail",
      front: { status: getStatus(data?.front?.status || progressStates.myKadFront) },
      back: { status: getStatus(data?.back?.status || progressStates.myKadBack) },
      blacklist: getStatus(progressStates.blacklist),
      kenalID: getStatus(progressStates.kenalID),
      selfie: getStatus(data?.selfie || progressStates.selfieWithMyKad),
      hologram: getStatus(data?.hologram || progressStates.hologramDetection),
      // The same sides as reported, Pending kept: where "Try Again" goes after a timeout.
      sideStatuses: {
        myKadFront: data?.front?.status || progressStates.myKadFront,
        myKadBack: data?.back?.status || progressStates.myKadBack,
        selfieWithMyKad: data?.selfie || progressStates.selfieWithMyKad,
        hologramDetection: data?.hologram || progressStates.hologramDetection,
      },
    };
    onFail(failResult);
  };

  return (
    <div
      style={{
        background: pageBgColor,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        padding: "1rem",
        height: "100%",
      }}
    >
      <div
        style={{
          background: cardBgColor,
          opacity: cardOpacity,
          padding: "2rem",
          borderRadius: "1rem",
          marginTop: "calc(100vh / 16)",
          marginBottom: "2rem",
        }}
      >
        <div className="flex flex-col">
          <SafeHTML html={getTitleHTML()} />
          <SafeHTML html={getDescriptionHTML()} />
        </div>
        <div className="flex flex-col items-center mt-8">
          <MagnifyingGlass
            height="80"
            width="80"
            color={highlightTextColor}
          />
          {progress >= 0 && (
            <div className="w-full max-w-xs mt-6">
              <div className="flex justify-between mb-1">
                <span
                  className="text-sm font-medium"
                  style={{ color: highlightTextColor }}
                >
                  Progress
                </span>
                <span
                  className="text-sm font-medium"
                  style={{ color: highlightTextColor }}
                >
                  {progress}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="h-2.5 rounded-full transition-all duration-500"
                  style={{
                    width: `${progress}%`,
                    backgroundColor: highlightTextColor,
                  }}
                ></div>
              </div>
            </div>
          )}
          {showLongWaitMessage && (
            <p className="mt-4 text-sm text-center text-gray-500 animate-pulse">
              This is taking longer than usual. Please wait...
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-1" />

      {showFooter && (
        <Footer
          style={{
            fontSize: "0.75rem",
            color: textColor,
            marginTop: "1rem",
          }}
        />
      )}
    </div>
  );
};

export default Verifying;
